import { type Resource, type TOptions, init, t as translate } from 'i18next'

const FALLBACK_LOCALE = 'en_US'

const LOCALE_IMPORTERS: Record<string, () => Promise<{ default: Record<string, unknown> }>> = {
  en_US: () => import('../../i18n/en_US.json'),
  en_AU: () => import('../../i18n/en_AU.json'),
  en_CA: () => import('../../i18n/en_CA.json'),
  en_GB: () => import('../../i18n/en_GB.json'),
  en_IE: () => import('../../i18n/en_IE.json'),
  fr_CA: () => import('../../i18n/fr_CA.json'),
  fr_FR: () => import('../../i18n/fr_FR.json'),
  de_DE: () => import('../../i18n/de_DE.json'),
  it_IT: () => import('../../i18n/it_IT.json'),
  pt_BR: () => import('../../i18n/pt_BR.json'),
  es_MX: () => import('../../i18n/es_MX.json'),
  es_ES: () => import('../../i18n/es_ES.json'),
  ja_JP: () => import('../../i18n/ja_JP.json'),
  zh_CN: () => import('../../i18n/zh_CN.json'),
  ko_KR: () => import('../../i18n/ko_KR.json'),
}

/**
 * Normalises a browser-style locale tag (e.g. `en-US`, `fr-FR`) to the
 * underscore-delimited format used as resource keys (e.g. `en_US`, `fr_FR`).
 */
export function normalizeLocale(lng: string): string {
  return lng.replace(/-/g, '_')
}

/**
 * Initialises i18next with only the resources for the requested locale and
 * the fallback locale (`en_US`). Unknown locales gracefully fall back to
 * `en_US` only.
 *
 * @param locale - Browser-style locale string (e.g. `'fr-FR'`, `'en-US'`).
 */
export async function initI18n(locale: string): Promise<void> {
  const normalized = normalizeLocale(locale)
  const { default: fallbackData } = await LOCALE_IMPORTERS[FALLBACK_LOCALE]()
  const resources: Resource = {
    [FALLBACK_LOCALE]: { translation: fallbackData },
  }
  if (normalized !== FALLBACK_LOCALE && LOCALE_IMPORTERS[normalized]) {
    const { default: localeData } = await LOCALE_IMPORTERS[normalized]()
    resources[normalized] = { translation: localeData }
  }
  await init({
    lng: normalized,
    fallbackLng: FALLBACK_LOCALE,
    interpolation: { escapeValue: false },
    keySeparator: false,
    nsSeparator: false,
    resources,
  })
}

/**
 * Translates a key using the current i18next instance.
 *
 * @param key - The translation key (e.g. `'gallery.header.expiryNoDate'`).
 * @param defaultValue - Optional English fallback returned when the key is absent.
 * @param options - Optional interpolation options passed to i18next.
 * @returns The translated string, or `defaultValue` (if provided) or the key itself when no translation exists.
 */
export function t(key: string, defaultValue?: string, options?: TOptions): string {
  const merged = { ...(options as Record<string, unknown>), ...(defaultValue !== undefined ? { defaultValue } : {}) }
  return translate(key, merged)
}

/**
 * Alias for {@link t}. Useful when the calling context semantically
 * reads better as "get a translation" rather than "translate".
 */
export function getTranslation(key: string, defaultValue?: string, options?: TOptions): string {
  return t(key, defaultValue, options)
}

/**
 * Returns the key unchanged. The function is typed so TypeScript can
 * catch typos at the call-site when string-literal keys are used.
 * The optional `defaultValue` parameter is accepted for API parity with
 * `t()` and `getTranslation()` but has no effect — the key is always returned.
 *
 * @param key - A valid i18n key string.
 * @param _defaultValue - Accepted for signature parity; not used.
 * @returns The same key value.
 */
export function asTranslationKey(key: string, _defaultValue?: string): string {
  return key
}
